/**
 * Field Visibility Configuration
 * Architecture v1 - Implementation Assets
 * 
 * Separation:
 * - MAIN_FRONTEND: Core fields shown in primary account view
 * - ADVANCED_FRONTEND: Detailed fields shown in expanded/modal views
 * - BACKEND_ONLY: Never sent to frontend, internal processing only
 */

// =============================================================================
// MAIN FRONTEND FIELDS
// Core fields displayed in primary account listing/cards
// =============================================================================

export const MAIN_FRONTEND_FIELDS = [
  { field: 'account_name', display_label: 'Account Name' },
  { field: 'account_number_masked', display_label: 'Account #' },
  { field: 'bureau_code', display_label: 'Bureau' },
  { field: 'account_type', display_label: 'Account Type' },
  { field: 'responsibility', display_label: 'Responsibility' },
  { field: 'open_closed', display_label: 'Open/Closed' },
  { field: 'account_status', display_label: 'Status' },
  { field: 'payment_status', display_label: 'Payment Status' },
  { field: 'balance', display_label: 'Balance', format: 'currency' },
  { field: 'credit_limit', display_label: 'Credit Limit', format: 'currency' },
  { field: 'past_due', display_label: 'Past Due', format: 'currency' },
  { field: 'monthly_payment', display_label: 'Monthly Payment', format: 'currency' },
  { field: 'date_opened', display_label: 'Date Opened', format: 'date' },
  { field: 'last_reported', display_label: 'Last Reported', format: 'date' },
  { field: 'utilization_percent', display_label: 'Utilization', format: 'percentage' },
  // Display flags for frontend tabs/filtering
  { field: 'is_negative', display_label: 'Negative Item' },
  { field: 'is_open', display_label: 'Is Open' },
  // Legacy field names for backward compatibility
  { field: 'creditor_name', display_label: 'Creditor Name' },
  { field: 'masked_account_number', display_label: 'Account Number' },
  { field: 'status', display_label: 'Status' },
  { field: 'bureau', display_label: 'Bureau' },
  { field: 'limit', display_label: 'Credit Limit', format: 'currency' },
  { field: 'date_reported', display_label: 'Date Reported', format: 'date' },
  { field: 'original_creditor', display_label: 'Original Creditor' },
  { field: 'tradeline_id', display_label: 'Tradeline ID' },
];

// =============================================================================
// ADVANCED FRONTEND FIELDS
// Detailed fields shown in expanded views, modals, or detail pages
// =============================================================================

export const ADVANCED_FRONTEND_FIELDS = [
  { field: 'account_number', display_label: 'Full Account Number' },
  { field: 'creditor_information', display_label: 'Creditor Contact' },
  { field: 'current_reporter', display_label: 'Current Reporter' },
  { field: 'original_creditor', display_label: 'Original Creditor' },
  { field: 'purchased_from', display_label: 'Purchased From' },
  { field: 'sold_to', display_label: 'Sold To' },
  { field: 'account_type_detail', display_label: 'Type Detail' },
  { field: 'loan_type', display_label: 'Loan Type' },
  { field: 'creditor_classification', display_label: 'Creditor Classification' },
  { field: 'activity_designator', display_label: 'Activity' },
  { field: 'original_balance', display_label: 'Original Balance', format: 'currency' },
  { field: 'high_credit', display_label: 'High Credit', format: 'currency' },
  { field: 'highest_balance', display_label: 'Highest Balance', format: 'currency' },
  { field: 'available_credit', display_label: 'Available Credit', format: 'currency' },
  { field: 'terms', display_label: 'Terms' },
  { field: 'terms_frequency', display_label: 'Payment Frequency' },
  { field: 'term_duration', display_label: 'Term Duration', format: 'number' },
  { field: 'months_reviewed', display_label: 'Months Reviewed', format: 'number' },
  { field: 'balance_updated', display_label: 'Balance Updated', format: 'date' },
  { field: 'status_updated', display_label: 'Status Updated', format: 'date' },
  { field: 'date_last_active', display_label: 'Last Active', format: 'date' },
  { field: 'date_of_last_payment', display_label: 'Last Payment', format: 'date' },
  { field: 'date_of_first_delinquency', display_label: 'First Delinquency', format: 'date' },
  { field: 'remarks', display_label: 'Remarks' },
  { field: 'comments', display_label: 'Comments' },
  { field: 'consumer_statement', display_label: 'Consumer Statement' },
  { field: 'statement', display_label: 'Statement' },
  { field: 'payment_history_grid', display_label: 'Payment History' },
  { field: 'payment_history_by_bureau', display_label: 'Payment History by Bureau' },
  { field: 'age_months', display_label: 'Account Age', format: 'number' },
  { field: 'days_since_last_payment', display_label: 'Days Since Payment', format: 'number' },
  { field: 'total_late_count', display_label: 'Total Lates', format: 'number' },
  { field: 'max_delinquency_code', display_label: 'Max Delinquency' },
  { field: 'consecutive_current_months', display_label: 'Consecutive Current', format: 'number' },
  { field: 'has_inconsistent_history', display_label: 'History Inconsistent' },
  { field: 'is_disputed', display_label: 'Disputed' },
  { field: 'dispute_status', display_label: 'Dispute Status' },
];

// =============================================================================
// BACKEND ONLY FIELDS
// Never sent to frontend - internal processing, AI, and audit only
// =============================================================================

export const BACKEND_ONLY_FIELDS = [
  // Classification (internal only)
  'classification',
  'is_collection',
  'is_charge_off',
  'has_late_payments',
  'is_installment',
  'is_revolving',
  
  // Dispute Internal
  'dispute_round',
  'dispute_strategy',
  'dispute_reasons',
  'last_dispute_date',
  'dispute_response_date',
  'dispute_outcome',
  
  // Audit Trail
  'created_at',
  'updated_at',
  'source_document_id',
  'parse_version',
  'raw_text_snapshot',
  'field_confidence',
  'manual_overrides',
  
  // AI Analysis
  'ai_risk_tag',
  'ai_dispute_priority',
  'ai_recommended_reasons',
  'ai_success_probability',
  'ai_analysis_notes',
  
  // Conflict Detection
  'bureau_conflicts',
  
  // History
  'dispute_history',
  
  // Documents
  'supporting_documents',
];

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export const getFieldVisibility = (field) => {
  if (MAIN_FRONTEND_FIELDS.find(f => f.field === field)) return 'main_frontend';
  if (ADVANCED_FRONTEND_FIELDS.find(f => f.field === field)) return 'advanced_frontend';
  if (BACKEND_ONLY_FIELDS.includes(field)) return 'backend_only';
  return 'backend_only'; // Default to backend-only for unknown fields
};

export const getDisplayLabel = (field) => {
  const mainField = MAIN_FRONTEND_FIELDS.find(f => f.field === field);
  if (mainField) return mainField.display_label;
  
  const advancedField = ADVANCED_FRONTEND_FIELDS.find(f => f.field === field);
  if (advancedField) return advancedField.display_label;
  
  // Fallback: convert snake_case to Title Case
  return field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

export const getFieldFormat = (field) => {
  const mainField = MAIN_FRONTEND_FIELDS.find(f => f.field === field);
  if (mainField) return mainField.format;
  
  const advancedField = ADVANCED_FRONTEND_FIELDS.find(f => f.field === field);
  if (advancedField) return advancedField.format;
  
  return undefined;
};

export const getMainFrontendFields = () => 
  MAIN_FRONTEND_FIELDS.map(f => f.field);

export const getAdvancedFrontendFields = () => 
  ADVANCED_FRONTEND_FIELDS.map(f => f.field);

export const getAllFrontendFields = () => [
  ...getMainFrontendFields(),
  ...getAdvancedFrontendFields(),
];
